<template>
  <manual-page
    title="Gestión de Clientes"
    description="Aprende a administrar clientes, ver su información y gestionar sus planes."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'clientsIndex' }"
    action-text="Ir a clientes"
  >
    <!-- Overview -->
    <h2>Vista General de Clientes</h2>
    <p>
      La sección de clientes te permite ver y gestionar todos los clientes registrados en la plataforma.
      Cada cliente tiene asociadas sus mascotas, planes de alimentación, órdenes y datos de facturación.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">En esta sección podrás:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Ver la lista completa de clientes</li>
        <li>Buscar clientes por nombre, email o teléfono</li>
        <li>Filtrar por sucursal o estado del plan</li>
        <li>Acceder al detalle de cada cliente</li>
        <li>Registrar nuevos clientes manualmente</li>
        <li>Editar información de clientes existentes</li>
      </ul>
    </div>

    <!-- Client List -->
    <h2>Lista de Clientes</h2>
    <p>
      La lista muestra todos los clientes con información resumida:
    </p>
    <ul>
      <li><strong>Nombre:</strong> Nombre completo del cliente</li>
      <li><strong>Email:</strong> Correo electrónico de contacto</li>
      <li><strong>Teléfono:</strong> Número de contacto</li>
      <li><strong>Sucursal:</strong> Franquicia asignada</li>
      <li><strong>Mascotas:</strong> Número de mascotas registradas</li>
      <li><strong>Estado:</strong> Si tiene plan activo o no</li>
    </ul>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Usa la barra de búsqueda para encontrar clientes rápidamente.
        Puedes buscar por nombre, email o teléfono.
      </p>
    </div>

    <!-- Client Detail -->
    <h2>Detalle del Cliente</h2>
    <p>Al hacer clic en un cliente, accedes a su información completa:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información Personal</h3>
          <p class="text-gray-600">
            Datos de contacto, dirección, fecha de nacimiento, y sucursal asignada.
            Puedes editar esta información si es necesario.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Mascotas</h3>
          <p class="text-gray-600">
            Lista de mascotas registradas por el cliente. Puedes ver el detalle de cada
            mascota, su dieta actual y solicitudes de cotización.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Planes Activos</h3>
          <p class="text-gray-600">
            Planes de alimentación del cliente, su estado (activo, pausado, cancelado),
            frecuencia de entrega y próximas órdenes programadas.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Historial de Órdenes</h3>
          <p class="text-gray-600">
            Todas las órdenes del cliente, tanto de alimento como de productos de la tienda.
            Puedes ver el estado de cada orden y su detalle.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Direcciones</h3>
          <p class="text-gray-600">
            Direcciones de entrega registradas. El cliente puede tener varias direcciones
            y elegir una diferente para cada entrega.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">6</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Métodos de Pago</h3>
          <p class="text-gray-600">
            Tarjetas registradas para cobros automáticos. Por seguridad, solo se muestran
            los últimos 4 dígitos de cada tarjeta.
          </p>
        </div>
      </div>
    </div>

    <!-- Impersonate -->
    <h2>Impersonar Cliente</h2>
    <p>
      Esta función te permite ver la app exactamente como la ve el cliente:
    </p>
    <ol>
      <li>Accede al detalle del cliente</li>
      <li>Haz clic en "Ver como cliente"</li>
      <li>Se abrirá la app del cliente con su sesión</li>
      <li>Podrás ver exactamente lo que ve el cliente</li>
      <li>Usa "Salir de impersonación" para volver</li>
    </ol>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Útil para:</strong> Diagnóstico de problemas, soporte técnico,
        verificar configuraciones y entender lo que ve el cliente.
      </p>
    </div>

    <!-- Creating Client -->
    <h2>Registrar Nuevo Cliente</h2>
    <p>
      Aunque la mayoría de clientes se registran desde la app, puedes crear clientes manualmente:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Datos Requeridos</h3>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Nombre y apellido</li>
            <li>Correo electrónico (único)</li>
            <li>Teléfono de contacto</li>
            <li>Código postal (para asignar sucursal)</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Asignación de Sucursal</h3>
          <p class="text-gray-600">
            El sistema asigna automáticamente la sucursal más cercana según el código postal.
            Puedes cambiar la sucursal manualmente si es necesario.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Los clientes creados manualmente deberán completar
        su registro desde la app para poder agregar mascotas y activar planes.
      </p>
    </div>

    <!-- Common Actions -->
    <h2>Acciones Comunes</h2>

    <h3>Cambiar Sucursal de un Cliente</h3>
    <p>
      Si un cliente se mudó o desea cambiar de sucursal:
    </p>
    <ol>
      <li>Abre el detalle del cliente</li>
      <li>Haz clic en "Editar"</li>
      <li>Cambia el código postal o selecciona la nueva sucursal</li>
      <li>Guarda los cambios</li>
    </ol>

    <h3>Ver Problemas de Pago</h3>
    <p>
      Si un cliente tiene problemas de pago, verás un indicador en su perfil.
      Puedes ver el detalle del problema y contactar al cliente para resolverlo.
    </p>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>
      Si tienes dudas sobre la gestión de clientes:
    </p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualClients",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualPets", label: "Mascotas" },
        { route: "manualPlans", label: "Planes" },
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualFranchises", label: "Franquicias" },
      ],
    };
  },
};
</script>
